import React, { useState } from 'react'
import { CgTrash } from 'react-icons/cg'
import { MdBlock } from 'react-icons/md'

import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { DeleteDialogBanner } from 'common/components/DeleteDialogBanner/DeleteDialogBanner'

import {
  useDeleteCommunityReplyMutation,
  useFetchCommunityMemberEmailsQuery,
  useSetMemberBannedMutation,
} from '../communityApi'

// The moderation a coach can do from the feed itself, matching the app's "⋯"
// menu on a post or reply: ban the author, delete a reply, and see who a
// member is. Bans made here are the same write the moderation page makes, so
// the member turns up under Banned members there and can be unbanned.

export function BanMemberButton({ coachOrgId, userId, name, className }) {
  const { createAlert } = useAlert()
  const [setMemberBanned] = useSetMemberBannedMutation()
  const [loading, setLoading] = useState(false)

  const handleBan = async () => {
    setLoading(true)
    const result = await setMemberBanned({ coachOrgId, userId, isBanned: true })
    setLoading(false)
    if (result?.error || result?.data?.error) {
      createAlert({ text: 'Could not ban member. Please try again.', type: 'danger' })
      return
    }
    createAlert({ text: 'Member banned. You can unban them from Moderation.', type: 'success' })
  }

  return (
    <Dialog>
      <DialogTrigger title='Ban member' className={className}>
        <MdBlock className='w-[18px] h-[18px]' />
      </DialogTrigger>
      <DialogContent>
        <DeleteDialogBanner
          titleText={`Ban ${name || 'this member'}?`}
          text='Their posts and replies will be hidden from the community. They can no longer post.'
          subtext='You can unban them from Moderation at any time, which restores their posts.'
          deleteText='Ban'
          handleDelete={handleBan}
          loading={loading}
        />
      </DialogContent>
    </Dialog>
  )
}

export function DeleteReplyButton({ coachOrgId, postId, replyId, className }) {
  const { createAlert } = useAlert()
  const [deleteReply] = useDeleteCommunityReplyMutation()
  const [loading, setLoading] = useState(false)

  const handleDelete = async () => {
    setLoading(true)
    const result = await deleteReply({ coachOrgId, postId, replyId })
    setLoading(false)
    if (result?.error || result?.data?.error) {
      createAlert({ text: 'Could not delete reply. Please try again.', type: 'danger' })
      return
    }
    createAlert({ text: 'Reply deleted', type: 'success' })
  }

  return (
    <Dialog>
      <DialogTrigger title='Delete reply' className={className}>
        <CgTrash className='w-[18px] h-[18px]' />
      </DialogTrigger>
      <DialogContent>
        <DeleteDialogBanner
          titleText='Delete this reply?'
          text='It disappears from the thread for every member.'
          subtext="This can't be undone."
          handleDelete={handleDelete}
          loading={loading}
        />
      </DialogContent>
    </Dialog>
  )
}

// A member's name, which opens a card with their email so the coach can reach
// them outside the community. A coach's own name is plain text.
export function MemberName({ coachOrgId, userId, name, isCoach, className }) {
  const [open, setOpen] = useState(false)
  const label = name || 'Member'

  if (isCoach) {
    return <span className={className}>{label}</span>
  }

  return (
    <Dialog open={open} setOpen={setOpen}>
      <DialogTrigger title='See member details' className={`${className} hover:underline text-left`}>
        {label}
      </DialogTrigger>
      <DialogContent>{open && <MemberCard coachOrgId={coachOrgId} userId={userId} name={label} />}</DialogContent>
    </Dialog>
  )
}

function MemberCard({ coachOrgId, userId, name }) {
  // The whole org's emails in one read, so the second card opens with no wait.
  const { data: emails } = useFetchCommunityMemberEmailsQuery({ coachOrgId }, { skip: !coachOrgId })
  const email = emails === undefined ? undefined : emails?.[userId] || null

  return (
    <div className='p-2'>
      <h3 className='font-bold text-xl text-tBlack'>{name}</h3>
      <p className='text-sm text-gray-500 mt-1'>Member of your community</p>
      <div className='mt-4 text-sm'>
        {email === undefined ? (
          <span className='text-gray-400'>Loading email…</span>
        ) : email ? (
          <a href={`mailto:${email}`} className='text-tGreen font-semibold hover:underline'>
            {email}
          </a>
        ) : (
          <span className='text-gray-500'>No email on file. It is saved the next time they open the app.</span>
        )}
      </div>
    </div>
  )
}
